import { settingsPayload } from "../../landingEditor/editorUtils";
import { isManagedLandingAsset } from "../../../../utils/landingAssets";

function draftSettingsPayload(form, productsEnabled) {
  const payload = settingsPayload(form, productsEnabled);
  delete payload.publicStatus;
  return payload;
}

function deploymentFromPublishResponse(data = {}) {
  const source = data.deployment || data.deploymentStatus || null;
  const deployment = typeof source === "string" ? { status: source } : source;
  const refreshQueued =
    data.publication?.deploymentQueued ??
    data.socialPreview?.queued ??
    deployment?.refreshQueued;

  if (!deployment && refreshQueued == null) return null;
  return {
    ...(deployment || {}),
    refreshQueued: refreshQueued === true,
  };
}

function deploymentJobFromPublishResponse(data = {}) {
  const source =
    data.deploymentJob ||
    data.publication?.deploymentJob ||
    data.socialPreview?.job ||
    null;
  if (!source || typeof source !== "object") return null;
  return source;
}

function deploymentJobId(job) {
  return String(job?.id || job?._id || "").trim();
}

function isPendingDeploymentJob(job) {
  return ["queued", "running"].includes(String(job?.status || "").trim().toLowerCase());
}

function deploymentJobFailure(job) {
  if (String(job?.status || "").trim().toLowerCase() !== "failed") return "";
  return String(job?.error?.message || "El despliegue no pudo completarse.").trim();
}

function openPreviewPlaceholder() {
  const previewWindow = window.open("about:blank", "_blank");
  if (!previewWindow) return null;

  try {
    previewWindow.document.title = "Preparando vista previa segura";
    previewWindow.document.body.textContent = "Preparando la vista previa del borrador…";
    Object.assign(previewWindow.document.body.style, {
      margin: "0",
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: "24px",
      boxSizing: "border-box",
      fontFamily: "Montserrat, Arial, sans-serif",
      fontWeight: "700",
      color: "#19242F",
      background: "#F4F5F7",
      textAlign: "center",
    });
    previewWindow.opener = null;
    previewWindow.focus();
  } catch {
    // La navegación final sigue siendo válida aunque el placeholder no se pueda decorar.
  }

  return previewWindow;
}

function collectManagedAssets(value, output = new Set()) {
  if (typeof value === "string") {
    if (isManagedLandingAsset(value)) output.add(value);
    return output;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectManagedAssets(item, output));
    return output;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectManagedAssets(item, output));
  }
  return output;
}

function sameEditorValue(left, right) {
  if (left === right) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((item, index) => sameEditorValue(item, right[index]));
  }

  if (
    (left && typeof left === "object") ||
    (right && typeof right === "object")
  ) {
    if (
      !left ||
      !right ||
      typeof left !== "object" ||
      typeof right !== "object" ||
      Array.isArray(left) ||
      Array.isArray(right)
    ) {
      return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        sameEditorValue(left[key], right[key])
    );
  }

  return false;
}

function preserveVersionMetadata(snapshot = {}, current = {}) {
  return {
    ...snapshot,
    revision: current.revision ?? snapshot.revision,
    draftRevision: current.draftRevision ?? snapshot.draftRevision,
    publishedRevision:
      current.publishedRevision ?? snapshot.publishedRevision,
    publicStatus: current.publicStatus ?? snapshot.publicStatus,
  };
}

export {
  draftSettingsPayload,
  deploymentFromPublishResponse,
  deploymentJobFromPublishResponse,
  deploymentJobId,
  isPendingDeploymentJob,
  deploymentJobFailure,
  openPreviewPlaceholder,
  collectManagedAssets,
  sameEditorValue,
  preserveVersionMetadata
};
